import React from "react";

import { Observation } from "@medplum/fhirtypes";

import { LoadingOverlay } from "@mantine/core";

import {
  ClientPostpartumFlowsheetForms,
  NewbornPostpartumFlowsheetForms,
} from "@/encounters/components/EncounterLabor/constants/postpartum-flowsheets";
import AssessmentNoteRow from "@/encounters/components/EncounterLabor/EncounterLaborPostpartum/ClientPostpartum/TableRows/AssessmentNoteRow";
import ClientCumulativeBloodLossRow from "@/encounters/components/EncounterLabor/EncounterLaborPostpartum/ClientPostpartum/TableRows/ClientCumulativeBloodLossRow";
import ClientEstimatedBloodLossRow from "@/encounters/components/EncounterLabor/EncounterLaborPostpartum/ClientPostpartum/TableRows/ClientEstimatedBloodLossRow";
import ClientInputOutputRow from "@/encounters/components/EncounterLabor/EncounterLaborPostpartum/ClientPostpartum/TableRows/ClientInputOutputRow";
import ClientPositionRow from "@/encounters/components/EncounterLabor/EncounterLaborPostpartum/ClientPostpartum/TableRows/ClientPositionRow";
import CordClampedRow from "@/encounters/components/EncounterLabor/EncounterLaborPostpartum/ClientPostpartum/TableRows/CordClampedRow";
import FirstLatchRow from "@/encounters/components/EncounterLabor/EncounterLaborPostpartum/ClientPostpartum/TableRows/FirstLatchRow";
import InitialIppAssessmentRow from "@/encounters/components/EncounterLabor/EncounterLaborPostpartum/ClientPostpartum/TableRows/InitialIppAssessmentRow";
import IppVitalsRow from "@/encounters/components/EncounterLabor/EncounterLaborPostpartum/ClientPostpartum/TableRows/IppVitalsRow";
import MedsAndTherapiesRow from "@/encounters/components/EncounterLabor/EncounterLaborPostpartum/ClientPostpartum/TableRows/Meds&TherapiesRow";
import PerineumRow from "@/encounters/components/EncounterLabor/EncounterLaborPostpartum/ClientPostpartum/TableRows/PerineumRow";
import PlacentaRow from "@/encounters/components/EncounterLabor/EncounterLaborPostpartum/ClientPostpartum/TableRows/PlacentaRow";
import PostpartumHemorrhageRow from "@/encounters/components/EncounterLabor/EncounterLaborPostpartum/ClientPostpartum/TableRows/PostpartumHemorrhageRow";
import { PostpartumTableRows } from "@/encounters/components/EncounterLabor/EncounterLaborPostpartum/PostpartumTable/PostpartumTableRows";
import DateRow from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/Common/DateRow";
import AdmissionDischargeRow from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/LaborObservationRow/AdmissionDischargeRow";
import BirthDateTimeRow from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/LaborObservationRow/BirthDateTimeRow";
import BirthingRow from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/LaborObservationRow/BirthingRow";
import ResuscitationRow from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/LaborObservationRow/ResuscitationRow";
import { usePostpartumRows } from "@/encounters/components/EncounterLabor/hooks/usePostpartumRows";
import { PostpartumRow } from "@/encounters/components/EncounterLabor/types/postpartum";
import { LOINCCodes } from "@/fhir/enums/loinc";
import { QuiltedHealthCodes } from "@/fhir/enums/quilted-health";

const ROW_COMPONENT_BY_FORM = {
  [ClientPostpartumFlowsheetForms.ClientBirthing]: BirthingRow,
  [ClientPostpartumFlowsheetForms.ClientResuscitation]: ResuscitationRow,
  [ClientPostpartumFlowsheetForms.ClientIppVitals]: IppVitalsRow,
  [ClientPostpartumFlowsheetForms.ClientPlacenta]: PlacentaRow,
  [ClientPostpartumFlowsheetForms.ClientPostpartumHemorrhage]: PostpartumHemorrhageRow,
  [ClientPostpartumFlowsheetForms.ClientPosition]: ClientPositionRow,
  [ClientPostpartumFlowsheetForms.ClientMedsTherapies]: MedsAndTherapiesRow,
  [ClientPostpartumFlowsheetForms.ClientInputOutput]: ClientInputOutputRow,
  [ClientPostpartumFlowsheetForms.ClientPerineum]: PerineumRow,
  [ClientPostpartumFlowsheetForms.ClientInitialIppAssessment]: InitialIppAssessmentRow,
  [ClientPostpartumFlowsheetForms.ClientFirstLatch]: FirstLatchRow,
  [ClientPostpartumFlowsheetForms.ClientCordCutClamped]: CordClampedRow,
  [ClientPostpartumFlowsheetForms.ClientAssessmentNote]: AssessmentNoteRow,
  [ClientPostpartumFlowsheetForms.ClientEstimatedBloodLoss]: ClientEstimatedBloodLossRow,
  [ClientPostpartumFlowsheetForms.ClientAdmissionDischarge]: AdmissionDischargeRow,
  // Cross-flowsheet mappings for shared forms (Cord Cut/Clamped and First Latch)
  [NewbornPostpartumFlowsheetForms.NewbornCordCutClamped]: CordClampedRow,
  [NewbornPostpartumFlowsheetForms.NewbornFirstLatch]: FirstLatchRow,
  [QuiltedHealthCodes.BirthDateTime]: BirthDateTimeRow,
  [LOINCCodes.CumulativeBloodLoss]: ClientCumulativeBloodLossRow,
};

interface ClientTableRowsProps {
  showNumbersOnly: boolean;
  onRefresh?: () => Promise<void>;
  isViewOnly: boolean;
}

export default function ClientTableRows({
  showNumbersOnly,
  onRefresh,
  isViewOnly,
}: ClientTableRowsProps) {
  const { rows, error, isLoading, refresh } = usePostpartumRows(ClientPostpartumFlowsheetForms);

  // If there's an error, we'll still show the existing rows but log the error
  if (error) {
    console.error("Error in ClientTableRows:", error);
  }

  // Combine the refresh functions
  const handleRefresh = async () => {
    await refresh();
    if (onRefresh) {
      await onRefresh();
    }
  };

  return (
    <>
      <LoadingOverlay visible={isLoading} />
      {rows
        .filter(([_, groups]) =>
          (groups as { observations: Observation[] }[]).some(
            (group) => group.observations.length > 0,
          ),
        )
        .map(([date, groups]: [string, PostpartumRow[]]) => (
          <React.Fragment key={date}>
            <DateRow date={date} />
            {groups.map((group) => (
              <PostpartumTableRows
                key={group.time}
                {...group}
                isViewOnly={isViewOnly}
                onRefresh={handleRefresh}
                flowsheetId="clientPostpartum"
                showNumbersOnly={showNumbersOnly}
                rowComponentByForm={ROW_COMPONENT_BY_FORM}
                flowsheetForms={ClientPostpartumFlowsheetForms}
              />
            ))}
          </React.Fragment>
        ))}
    </>
  );
}
